import type { BaseEnvelope } from './base-patch';
import type { AppliedFrame, AppliedNewEntity, AppliedTouchedRow } from '../transport/applied-frame';
import { applyAppliedFrame } from '../transport/apply-applied-frame';
import { encounterCreatureField } from '../../shared/array-types';
import { fromBaseRecord } from './from-base-context';
import { sameJson } from './json-change';

const revisionColumns = new Set(['movement_rev', 'encounter_creature_t', 'encounter_creature_t_created', 'encounter_creature_t_updated']);
const headers = new Set(['base_rev', 'catalog_ref', 'dependency_revs', 'encounter_creatures']);

/** A save echo may advance revision metadata while the first birth compile runs.
 * Carry that metadata into its result; changed rule inputs still supersede it. */
export function completePendingBirth(entity: AppliedNewEntity, compact: BaseEnvelope['base'],
    initial: Pick<BaseEnvelope, 'base' | 'base_rev' | 'metadata' | 'engine' | 'catalog_version'>,
    current: BaseEnvelope | undefined): { entity: AppliedNewEntity; revision?: AppliedTouchedRow } | undefined {
    if (!current) return;
    if (current.base === initial.base) return { entity: { ...entity, base: compact } };
    if (current.engine !== initial.engine || current.metadata !== initial.metadata || current.catalog_version !== initial.catalog_version
        || current.base_rev < initial.base_rev) return;
    const before = initial.base, after = current.base;
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)]))
        if (!headers.has(key) && !sameJson(before[key], after[key])) return;
    const a = before.encounter_creatures, b = after.encounter_creatures;
    if (!fromBaseRecord(a) || !fromBaseRecord(b)) return;
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)]))
        if (!revisionColumns.has(key) && !sameJson(encounterCreatureField(a, key), encounterCreatureField(b, key))) return;
    if (String(before.catalog_ref ?? '').replace(/:\d+$/, '') !== String(after.catalog_ref ?? '').replace(/:\d+$/, '')) return;
    const own = new Set([entity.entity_id.toUpperCase(), String(encounterCreatureField(a, 'encounter_id') ?? '').toUpperCase()]);
    const priorDeps = fromBaseRecord(before.dependency_revs) ? before.dependency_revs : {};
    const nextDeps = fromBaseRecord(after.dependency_revs) ? after.dependency_revs : {};
    for (const key of new Set([...Object.keys(priorDeps), ...Object.keys(nextDeps)]))
        if (!own.has(key.toUpperCase()) && !sameJson(priorDeps[key], nextDeps[key])) return;
    const row = Object.fromEntries([...revisionColumns].flatMap(key => {
        const value = encounterCreatureField(b, key);
        return value === undefined ? [] : [[key, value]];
    }));
    const revision: AppliedTouchedRow = { table: 'encounter_creatures', row_id: entity.entity_id, partial: true, row };
    const frame: AppliedFrame = { kind: 'applied', operation_id: 'birth-revision', encounter_id: '', actor_id: '', rev: 0,
        member_revs: { [entity.entity_id]: Number(encounterCreatureField(b, 'movement_rev') ?? 0) },
        rows: [revision], computed: {}, new_entities: [], deletes: [] };
    const member = applyAppliedFrame(frame, entity.member, false, false);
    const raw = applyAppliedFrame(frame, compact, true, false);
    if (!fromBaseRecord(member.value) || !fromBaseRecord(raw.value) || member.recover.length || raw.recover.length) return;
    return { revision, entity: { ...entity, base_rev: current.base_rev, member: member.value,
        base: { ...raw.value, base_rev: current.base_rev, catalog_ref: after.catalog_ref, dependency_revs: after.dependency_revs } } }; // dnr-clone: allow size=one incoming newborn base shell; why=carry only its saved revision header after the first compile
}
